/**
 * GET /crew/me/tasks/:id — the crew mobile task-detail screen.
 *
 * Wireframe: docs/wireframes/08-crew-mobile-task-detail.html
 *
 * A lean projection for the field: the task, its step position in the
 * project ("Step X of N"), the site context a crew needs on arrival
 * (address + a contact to call), the assigned crew, and the per-task
 * status timeline that drives the "this task" activity feed. Photo /
 * voice / punch capture (Pillar B.3) layer on top of this later.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { AddressSchema } from './locations.js';
import { PickupLocationSchema } from './tasks.js';
import { InstallSubtypeSchema, PickupSubtypeSchema, TaskStatusSchema, TaskTypeSchema, } from './enums.js';
export const CrewTaskCrewMemberSchema = z.object({
    display_name: z.string(),
    crew_role: z.string(),
    avatar_url: z.string().url().nullable(),
    is_lead: z.boolean(),
});
// The "This task" feed is a chronological mix of status transitions and
// free-text/voice notes, discriminated by `kind`.
export const CrewTaskTimelineStatusSchema = z.object({
    kind: z.literal('status'),
    id: UuidSchema,
    occurred_at: IsoDateTimeSchema,
    actor_name: z.string().nullable(),
    from_status: TaskStatusSchema.nullable(),
    to_status: TaskStatusSchema,
    note: z.string().nullable(),
});
export const CrewTaskTimelineNoteSchema = z.object({
    kind: z.literal('note'),
    id: UuidSchema,
    occurred_at: IsoDateTimeSchema,
    actor_name: z.string().nullable(),
    body: z.string(),
    source: z.enum(['text', 'voice']),
});
export const CrewTaskTimelineEntrySchema = z.discriminatedUnion('kind', [
    CrewTaskTimelineStatusSchema,
    CrewTaskTimelineNoteSchema,
]);
// POST /crew/me/tasks/:id/notes
export const CrewTaskNoteCreateSchema = z.object({
    body: z.string().min(1).max(2000),
    source: z.enum(['text', 'voice']).default('text'),
});
export const CrewTaskDetailResponseSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    project_title: z.string(),
    project_client_name: z.string(),
    /** 1-based position among the project's non-cancelled tasks (the "Step X of N"). */
    step_index: z.number().int().min(1),
    step_count: z.number().int().min(1),
    type: TaskTypeSchema,
    pickup_subtype: PickupSubtypeSchema.nullable(),
    install_subtype: InstallSubtypeSchema.nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: TaskStatusSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
    started_at: IsoDateTimeSchema.nullable(),
    completed_at: IsoDateTimeSchema.nullable(),
    estimated_duration_minutes: z.number().int().min(0).nullable(),
    /** Business-hours worked time (demo #8): net in_progress time clamped to the
     *  project's committed windows, so a task left running overnight reads the
     *  same here as in the recap. Null when there's no timeline to derive it. */
    worked_minutes: z.number().int().min(0).nullable(),
    sap_rma_reference: z.string().nullable(),
    site_address: AddressSchema.nullable(),
    /** Ordered pickup stops for a pickup task (warehouse or custom address). */
    pickup_locations: z.array(PickupLocationSchema).default([]),
    site_contact_name: z.string().nullable(),
    site_contact_phone: z.string().nullable(),
    /** The vehicle booked for this task (so crew can report a problem with it). */
    vehicle_id: UuidSchema.nullable(),
    vehicle_label: z.string().nullable(),
    /** True when the signed-in crew member leads this task. */
    viewer_is_lead: z.boolean(),
    crew: z.array(CrewTaskCrewMemberSchema),
    /** Most-recent-first status transitions; the "this task" activity feed. */
    timeline: z.array(CrewTaskTimelineEntrySchema),
});
//# sourceMappingURL=crew-task-detail.js.map